/**
 * PlanPage Component
 * Shows the user's current plan details, usage limits, features, and upgrade options.
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User, Rocket, Zap, Building2, Check, ArrowRight, Settings,
  Mic, Clock, HardDrive, Crown,
} from 'lucide-react';
import { PageLayout } from '@/components';
import { Chrome } from '@/components/layout';
import { AppPageHeader, Badge, Button, ErrorBanner } from '@/components/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCapabilities } from '@/contexts/CapabilitiesContext';
import { usePlans } from '@/contexts/PlansContext';
import { useTranslation } from 'react-i18next';

const ICON_MAP: Record<string, React.ElementType> = {
  user: User,
  rocket: Rocket,
  zap: Zap,
  building2: Building2,
};

const PLAN_STYLES: Record<string, {
  border: string;
  bg: string;
  badge: string;
  icon: string;
  iconBg: string;
}> = {
  slate: {
    border: 'border-slate-300',
    bg: 'bg-white',
    badge: 'bg-slate-100 text-slate-700',
    icon: 'text-slate-600',
    iconBg: 'bg-slate-100',
  },
  blue: {
    border: 'border-blue-400',
    bg: 'bg-blue-50/50',
    badge: 'bg-blue-100 text-blue-700',
    icon: 'text-blue-600',
    iconBg: 'bg-blue-100',
  },
  amber: {
    border: 'border-amber-400',
    bg: 'bg-amber-50/50',
    badge: 'bg-amber-100 text-amber-700',
    icon: 'text-amber-600',
    iconBg: 'bg-amber-100',
  },
  purple: {
    border: 'border-purple-400',
    bg: 'bg-purple-50/50',
    badge: 'bg-purple-100 text-purple-700',
    icon: 'text-purple-600',
    iconBg: 'bg-purple-100',
  },
};

const PlanPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const { planId, limits, usage, isProPlan, refreshCapabilities } = useCapabilities();
  const { getPlan, getPlanPrice, plans, error: plansError, refreshPlans } = usePlans();

  const currentPlan = getPlan(planId);
  const formattedPrice = getPlanPrice(planId);
  const colorTheme = currentPlan?.display?.colorTheme || 'slate';
  const styles = PLAN_STYLES[colorTheme] || PLAN_STYLES.slate;
  const PlanIcon = ICON_MAP[currentPlan?.display?.icon || 'user'] || User;

  // Redirect unauthenticated users
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Refresh capabilities on mount for fresh usage data
  useEffect(() => {
    if (isAuthenticated) {
      refreshCapabilities();
    }
  }, [isAuthenticated, refreshCapabilities]);

  const recordingsPerDay = limits.recordingsPerDay;
  const recordingsToday = usage.recordingsToday;
  const recordingProgress = recordingsPerDay === -1
    ? 0
    : Math.min(100, (recordingsToday / recordingsPerDay) * 100);

  // Determine which plans to show as upgrade options
  const upgradePlans = plans.filter(p => {
    if (planId === 'free') return p.planId === 'starter' || p.planId === 'pro';
    if (planId === 'starter') return p.planId === 'pro';
    return false;
  });

  return (
    <PageLayout variant="plain">
      <Chrome component="appPageHeader">
        <AppPageHeader
          icon={<PlanIcon className="w-4 h-4" />}
          badge={t('plan.badge')}
          title={`${currentPlan?.displayName || 'Free'} ${t('nav.plan')}`}
          subtitle={currentPlan?.description || t('plan.subtitle')}
        />
      </Chrome>

      <section className="py-8 sm:py-10 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 min-h-[50vh]">
        <div className="container mx-auto px-6 max-w-4xl">

          {/* Plans Error */}
          {plansError && (
            <ErrorBanner message={plansError} onRetry={refreshPlans} className="mb-8" />
          )}

          {/* Current Plan Summary */}
          {!plansError && (
            <div className={`rounded-xl border-2 ${styles.border} ${styles.bg} p-6 mb-8`}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${styles.iconBg} flex items-center justify-center`}>
                    <PlanIcon className={`w-6 h-6 ${styles.icon}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-slate-900">
                        {currentPlan?.displayName || 'Free'} {t('nav.plan')}
                      </h2>
                      <Badge variant={planId === 'pro' ? 'pro' : planId === 'starter' ? 'starter' : 'free'} size="sm">
                        {t('plan.active')}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {currentPlan?.isTrial
                        ? t('plan.freeTrial', { days: currentPlan.trialDays })
                        : formattedPrice
                          ? `${formattedPrice}${currentPlan?.priceDetail || ''}`
                          : t('plan.customPricing')
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Usage & Limits Grid */}
          <h3 className="text-base font-semibold text-slate-900 mb-4">{t('plan.usageLimits')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">

            {/* Recordings Today */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Mic className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-slate-700">{t('plan.recordingsToday')}</span>
              </div>
              <div className="text-2xl font-bold text-slate-900 mb-2">
                {recordingsPerDay === -1 ? (
                  t('plan.unlimited')
                ) : (
                  <>{recordingsToday} <span className="text-sm font-normal text-slate-400">/ {recordingsPerDay}</span></>
                )}
              </div>
              {recordingsPerDay !== -1 && (
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${recordingProgress >= 100 ? 'bg-red-500' : recordingProgress >= 75 ? 'bg-amber-500' : 'bg-blue-500'}`}
                    style={{ width: `${recordingProgress}%` }}
                  />
                </div>
              )}
            </div>

            {/* Max Recording Length */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-slate-700">{t('plan.maxLength')}</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {limits.maxRecordingMinutes === -1
                  ? t('plan.unlimited')
                  : t('plan.minutes', { count: limits.maxRecordingMinutes })
                }
              </div>
            </div>

            {/* Cloud Storage */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <HardDrive className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-slate-700">{t('plan.cloudStorage')}</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {limits.cloudStorageGb === -1
                  ? t('plan.unlimited')
                  : limits.cloudStorageGb === 0
                    ? t('plan.notIncluded')
                    : `${limits.cloudStorageGb} GB`
                }
              </div>
            </div>
          </div>

          {/* Features */}
          {currentPlan?.features && currentPlan.features.length > 0 && (
            <>
              <h3 className="text-base font-semibold text-slate-900 mb-4">{t('plan.features')}</h3>
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentPlan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2.5">
                      <Check className={`w-4 h-4 flex-shrink-0 ${styles.icon}`} />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {/* Upgrade CTA */}
          {upgradePlans.length > 0 && (
            <>
              <h3 className="text-base font-semibold text-slate-900 mb-4">{t('plan.upgradeTitle')}</h3>
              <div className={`grid grid-cols-1 ${upgradePlans.length > 1 ? 'sm:grid-cols-2' : ''} gap-4 mb-8`}>
                {upgradePlans.map(plan => {
                  const planStyles = PLAN_STYLES[plan.display?.colorTheme || 'slate'] || PLAN_STYLES.slate;
                  const UpgradeIcon = ICON_MAP[plan.display?.icon || 'rocket'] || Rocket;
                  return (
                    <div key={plan.planId} className={`rounded-xl border-2 ${planStyles.border} ${planStyles.bg} p-6`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg ${planStyles.iconBg} flex items-center justify-center`}>
                          <UpgradeIcon className={`w-5 h-5 ${planStyles.icon}`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{plan.displayName}</h4>
                          <p className="text-sm text-slate-500">{plan.description}</p>
                        </div>
                      </div>
                      <ul className="space-y-2 mb-4">
                        {plan.features.slice(0, 4).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                            <Check className={`w-3.5 h-3.5 ${planStyles.icon}`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button asChild variant="gradient" size="md" className="w-full">
                        <Link to="/upgrade">
                          {t('plan.upgradeTo', { plan: plan.displayName })}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Best plan message for Pro/Enterprise */}
          {isProPlan && (
            <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-xl border border-amber-200 p-6 mb-8 text-center">
              <Crown className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="font-semibold text-slate-900">{t('plan.bestPlan')}</p>
              <p className="text-sm text-slate-500 mt-1">{t('plan.bestPlanDesc')}</p>
            </div>
          )}

          {/* Account Settings Link */}
          <div className="text-center">
            <Button asChild variant="ghost" size="sm">
              <Link to="/account">
                <Settings className="w-4 h-4 mr-1.5" />
                {t('plan.accountSettings')}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default PlanPage;
